import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, CheckCircle, Quote } from 'lucide-react';
import SEO from '../components/SEO';
import LazyImage from '../components/LazyImage';

const CaseStudySliceVault = () => {
  const siteUrl = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || 'https://orboroi.com';
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "SliceVault Medical AI - Data Preparation & Model Evaluation Case Study",
    "description": "How Orboroi delivered 10,000+ medical images with 99%+ precision for SliceVault's medical AI platform through expert annotation and validation.",
    "image": "https://images.pexels.com/photos/7089298/pexels-photo-7089298.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    "datePublished": "2024-01-15",
    "dateModified": "2024-01-15",
    "author": {
      "@type": "Organization",
      "name": "Orboroi"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Orboroi",
      "logo": {
        "@type": "ImageObject",
        "url": `${siteUrl}/logo.png`
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${siteUrl}/case-study-slicevault`
    }
  };

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Case Studies", url: "/case-studies" },
    { name: "SliceVault", url: "/case-study-slicevault" }
  ];

  return (
    <div className="min-h-screen bg-black text-white pt-20">
      <SEO
        title="SliceVault Medical AI Case Study"
        description="How Orboroi delivered 10,000+ medical images with 99%+ precision for SliceVault's AI platform through expert annotation and validation services."
        canonical="/case-study-slicevault"
        type="article"
        author="Orboroi Team"
        datePublished="2024-01-15"
        structuredData={structuredData}
        breadcrumbs={breadcrumbs}
        image="https://images.pexels.com/photos/7089298/pexels-photo-7089298.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
      />
      {/* Breadcrumbs */}
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center gap-2 text-sm text-gray-400">
          <Link to="/" className="hover:text-[#379F7F]">Home</Link>
          <span>→</span>
          <Link to="/case-studies" className="hover:text-[#379F7F]">Case Studies</Link>
          <span>→</span>
          <span className="text-white">SliceVault</span>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-12 bg-gradient-to-b from-purple-900 to-indigo-900">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Case Study — <span className="text-[#379F7F]">SliceVault</span>
            </h1>
            <p className="text-xl text-gray-300 mb-2">Medical AI</p>
            <p className="text-gray-400">Data Preparation & Model Evaluation</p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 bg-black">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto space-y-12">
            
            {/* Challenge */}
            <div>
              <h2 className="text-3xl font-bold text-white mb-4">The Challenge</h2>
              <p className="text-lg text-gray-300 leading-relaxed">
                SliceVault required highly precise organ segmentation for CT, MRI, and X-ray images to power diagnostic AI models. Existing datasets were inconsistent and lacked the granularity needed for reliable clinical AI deployment.
              </p>
            </div>

            {/* Solution */}
            <div>
              <h2 className="text-3xl font-bold text-white mb-4">Our Solution</h2>
              <p className="text-lg text-gray-300 leading-relaxed mb-6">
                Performed semantic segmentation on 10,000+ medical images, annotating all human organs separately with 99%+ precision. Built detailed annotation guidelines to maintain consistency across modalities. Implemented HITL validation and multi-layer QA to ensure dataset reliability. Delivered iterative updates to support continuous model improvement.
              </p>
              <div className="case-study-image-container">
                <LazyImage 
                  src="https://images.pexels.com/photos/7089298/pexels-photo-7089298.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Medical AI image segmentation showing SliceVault's advanced annotation for human organ detection"
                  className="case-study-image"
                />
              </div>
            </div>

            {/* Results */}
            <div>
              <h2 className="text-3xl font-bold text-white mb-6">Results</h2>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-[#379F7F] mr-3 mt-1 flex-shrink-0" />
                  <span className="text-lg text-gray-300">Accelerated time-to-market for clinical AI deployment</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-[#379F7F] mr-3 mt-1 flex-shrink-0" />
                  <span className="text-lg text-gray-300">Achieved 99%+ annotation precision, enabling accurate AI predictions</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-[#379F7F] mr-3 mt-1 flex-shrink-0" />
                  <span className="text-lg text-gray-300">Reduced model training errors and improved diagnostic reliability</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-[#379F7F] mr-3 mt-1 flex-shrink-0" />
                  <span className="text-lg text-gray-300">Semantic segmentation on 10,000+ medical images</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-[#379F7F] mr-3 mt-1 flex-shrink-0" />
                  <span className="text-lg text-gray-300">HITL validation and multi-layer QA implemented</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-[#379F7F] mr-3 mt-1 flex-shrink-0" />
                  <span className="text-lg text-gray-300">Iterative updates delivered for continuous model improvement</span>
                </li>
              </ul>
            </div>

            {/* Testimonial */}
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-8">
              <Quote className="w-12 h-12 text-[#379F7F] mb-4" />
              <p className="text-xl text-gray-200 italic mb-6 leading-relaxed">
                "Orboroi delivered the level of precision and consistency we needed for medical AI. Their team integrated seamlessly into our workflow and ensured high-quality outputs at scale."
              </p>
              <div className="border-t border-gray-700 pt-4">
                <p className="text-white font-semibold">Lars Edenbrant</p>
                <p className="text-gray-400">Chief Medical Officer, SliceVault</p>
              </div>
            </div>

            {/* CTA */}
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-lg p-8 mt-12 text-center">
              <h3 className="text-2xl font-bold text-white mb-4">
                Ready to Scale Your AI with Data Certainty?
              </h3>
              <p className="text-gray-300 mb-6">
                Let's discuss how Orboroi can help accelerate your AI development.
              </p>
              <Link to="/contact">
                <Button className="bg-[#379F7F] text-black hover:bg-[#379F7F]/90 font-semibold">
                  Get in Touch
                </Button>
              </Link>
            </div>

            {/* Back Link */}
            <div className="pt-8">
              <Link to="/case-studies" className="text-[#379F7F] hover:underline inline-flex items-center">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to All Case Studies
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudySliceVault;
